import { useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { createContext, type PropsWithChildren, useContext } from 'react';
import { SparkSuspense } from '../components/spark-suspense';
import { useHandlers, useSite } from '../context/page-context';
import { useOptimisticMutation, type Variables } from '../hooks/use-optimistic-mutation';
import { isShopify, useEnv } from './env-context';

export type Settings = {
  allowCheckout: boolean;
  allowedSalesAgents: number;
  allowAddressEditing: boolean;
  allowDefaultBillingModification: boolean;
  allowLegacyRequestForQuotePaymentMethod: boolean;
  applyPriceBreaksAcrossVariants: boolean;
  baseCountryCode: string;
  baseCurrency: string;
  cartSearchType: 'basic' | 'full' | 'off';
  hideAICartFeatures: boolean;
  igniteBaseUrl?: string;
  igniteApiKey?: string;
  igniteFrontendAuthEndpoint?: string;
  igniteFrontendAuthEndpointMethod?: string;
  igniteFrontendJwtKey?: string;
  ignitePlatform:
    | 'wix'
    | 'custom'
    | 'bigcommerce'
    | 'shopify'
    | 'shopify_plus'
    | 'woocommerce'
    | 'magento';
  igniteBasicAuth?: Record<string, string>;
  integrationBrightpearl: boolean;
  integrationBrightpearlAccount: string;
  integrationBrightpearlCustomerExemptCodes: string;
  integrationBrightpearlCustomerTag: string;
  integrationBrightpearlOAuthCode: string;
  integrationBrightpearlSendInvite: boolean;
  integrationDear: boolean;
  integrationDearApiAccountId: string;
  integrationDearCustomerTag: string;
  integrationDearCustomerExemptTaxCodes: string;
  integrationDearSendInvite: boolean;
  integrationCin7: boolean;
  integrationCin7ApiAccountId: string;
  integrationCin7ApiApplicationKey: string;
  integrationCin7CustomerTag: string;
  integrationCin7AdditionalTags: string;
  integrationCin7CustomerExemptTaxCodes: string;
  integrationCin7SendInvite: boolean;
  integrationCin7DisableChildCustomers: boolean;
  integrationIntuitiveShipping: boolean;
  integrationIntuitiveShippingApiKey: string;
  integrationKatana: boolean;
  integrationKatanaOAuthCode: string;
  integrationKatanaSendInvite: boolean;
  integrationLinnworks: boolean;
  integrationLinnworksToken: string;
  integrationQuickBooks: boolean;
  integrationQuickBooksAutoContactCreationEnabled?: boolean | null;
  integrationQuickBooksPaymentOnAccountInvoiceEvent: string;
  integrationQuickBooksShippingTaxCode: string;
  integrationQuickBooksGenerateSalesReceiptForPaymentUpfrontEnabled: boolean;
  integrationQuickBooksIncomeAccountName: string;
  integrationQuickBooksTaxExemptionTaxCode: string;
  integrationQuickBooksOAuth:
    | {
        access_token: string;
        scope: string;
        expires_in: number;
        refresh_token: string;
        token_type: string;
      }
    | [];
  integrationQuickBooksOAuthCode: string;
  integrationQuickBooksRealmId: string;
  integrationSufio: boolean;
  integrationUnleashed: boolean;
  integrationUnleashedApiId: string;
  integrationUnleashedApiKey: string;
  integrationUnleashedCustomerTypes: string;
  integrationUnleashedPrefixedPriceLists: string;
  integrationUnleashedSendInvite: boolean;
  integrationUnleashedSellPriceTierExpectedCurrencyCodes: {
    tier1: string;
    tier2: string;
    tier3: string;
    tier4: string;
    tier5: string;
    tier6: string;
    tier7: string;
    tier8: string;
    tier9: string;
    tier10: string;
  };
  integrationXero: boolean;
  integrationXeroOAuthCode: string;
  integrationXeroOAuth:
    | {
        access_token: string;
        scope: string;
        expires_in: number;
        refresh_token: string;
        token_type: string;
      }
    | [];
  integrationXeroTenantId: string;
  integrationXeroAutoContactCreationEnabled: boolean;
  integrationXeroDefaultLineItemAccountCode: string;
  integrationXeroPaymentOnAccountInvoiceOn: string;
  integrationXeroInvoicesUseItemCodes: boolean;
  integrationXeroLineItemTracking: string;
  integrationXeroShippingLineAccountCode: string;
  internalVisiblePurchaseIdPostfix: string;
  internalVisiblePurchaseIdPrefix: string;
  invoicingEnabled: boolean;
  invoicingSettingContentZones: Record<
    string,
    {
      title?: string;
      content?: string;
    }
  >;
  localeGraphqlMiddlewareEnabled: boolean;
  ignoreTransactions: boolean;
  merchantAddress1: string;
  merchantAddress1Override: string;
  merchantAddress2: string;
  merchantAddress2Override: string;
  merchantCity: string;
  merchantCityOverride: string;
  merchantCoreDataOverride: boolean;
  merchantCountry: string;
  merchantCountryOverride: string;
  merchantEmail: string;
  merchantEmailOverride: string;
  merchantLocale: string;
  merchantPhone: string;
  merchantPhoneOverride: string;
  merchantPostcode: string;
  merchantPostcodeOverride: string;
  merchantRegion: string;
  merchantRegionOverride: string;
  merchantStoreName: string;
  merchantStoreNameOverride: string;
  merchantTimezone: string;
  merchantUrl: string;
  merchantUrlOverride: string;
  multiLocationStockConfig: {
    useDefault: boolean;
    allowedStockLocationIds: string[];
  };
  paymentOnAccountEnforceCreditLimit: boolean;
  paymentVisibilityEnabled: boolean;
  quoteNotificationEmails: string[];
  purchaseTimelineEnabled: boolean;
  purchaseNotificationEmails: string[];
  quotingEnabled: boolean;
  quotingEngine: {
    customer_create_quote: boolean;
  };
  shopifyDraftOrderCreateDuringCalc: boolean;
  shopifyPlus: boolean;
  shopifyIncreaseMaxCartLines: boolean;
  showPoweredBy: boolean;
  stockDisplay: {
    show_status: boolean;
    show_units: boolean;
    hide_preorder?: boolean;
    max: number;
    low: number;
    last: number;
  };
  subAccountsEnabled: boolean;
  useIgniteEndpointsForShopifyCheckout: boolean;
  useInternalShipping: boolean;
  useLegacyPriceListInheritance: number;
  useMultiLocationStock: boolean;
  usePurchasingAPI: boolean;
  useShopifyCheckout: boolean;
  dualWriteToCustomersAPI: boolean;
  useCustomersAPI: boolean;
  hasLaunched: boolean;
  draftOrdersAutoComplete: boolean;
  draftOrdersAutoCompleteAccountSetAsPaid: boolean;
  draftOrdersAutoCompleteInvoiceSetAsPaid: boolean;
  draftOrdersPriceOverrideEnabled: boolean;
  createDraftOrderOnCalculate: boolean;
  disableNewCustomerInvitation: boolean;
};

export const SHOPIFY_SETTING_KEYS = [
  'draftOrdersAutoComplete',
  'draftOrdersAutoCompleteAccountSetAsPaid',
  'draftOrdersAutoCompleteInvoiceSetAsPaid',
  'draftOrdersPriceOverrideEnabled',
  'createDraftOrderOnCalculate',
  'disableNewCustomerInvitation',
  'ignoreTransactions',
  'shippingAutoSelectionStrategy',
] as const;

export function isShopifySettingKey(key: string): key is (typeof SHOPIFY_SETTING_KEYS)[number] {
  return (SHOPIFY_SETTING_KEYS as readonly string[]).includes(key);
}

/** Keys of {@link Settings} whose value is a plain boolean (the `integration*` enable toggles). */
export type BooleanSettingKey = {
  [K in keyof Settings]-?: Settings[K] extends boolean ? K : never;
}[keyof Settings];

export type Setting = SettingsApiResponse[number];

export type SettingsApiResponse = {
  [K in keyof Required<Settings>]: { key: K; value: Settings[K] };
}[keyof Settings][];

const SETTINGS_QUERY_KEY = ['get-settings'];

export { SETTINGS_QUERY_KEY };

export function useUpdateSettings() {
  const { apis } = useHandlers();
  const site = useSite();
  const env = useEnv();
  const queryClient = useQueryClient();

  async function updateSettings(settings: Setting | Setting[]) {
    const settingsArray = Array.isArray(settings) ? settings : [settings];
    const shopifySettings: Setting[] = [];
    const generalSettings: Setting[] = [];

    for (const setting of settingsArray) {
      if (isShopifySettingKey(setting.key)) {
        shopifySettings.push(setting);
      } else {
        generalSettings.push(setting);
      }
    }

    if (shopifySettings.length && isShopify(env.platform)) {
      await apis.site.settings().updateShopify(site.siteId, site.siteEnv, shopifySettings);
    }

    if (generalSettings.length) {
      await apis.site.settings().updateMany(generalSettings);
    }
  }

  return useOptimisticMutation<Settings, Variables<typeof updateSettings>>({
    queryClient,
    queryKey: SETTINGS_QUERY_KEY,
    mutationFn: updateSettings,
    optimisticUpdate: (prev, settings) => {
      if (!prev) {
        return undefined;
      }

      const settingsArray = Array.isArray(settings) ? settings : [settings];
      const next = { ...prev } as Record<string, unknown>;
      for (const setting of settingsArray) {
        next[setting.key] = setting.value;
      }
      return next as Settings;
    },
  });
}

const SettingsContext = createContext<Settings | undefined>(undefined);

export function useSettings(): Settings {
  const context = useContext(SettingsContext);
  if (!context) throw new Error('useSettings must be used within an SettingsProvider');
  return context;
}

export function SettingsProvider({ children }: PropsWithChildren) {
  const { apis } = useHandlers();
  const site = useSite();
  const env = useEnv();

  async function fetchSettings() {
    const response = await apis.site.settings().list();

    const settings: Settings = Object.fromEntries(
      response.map((s) => [s.key, s.value]),
    ) as Settings;

    if (env.store_id) {
      try {
        const shopifyResponse = await apis.site.settings().listShopify(site.siteId, site.siteEnv);
        Object.assign(settings, Object.fromEntries(shopifyResponse.map((s) => [s.key, s.value])));
      } catch {
        // Ignore: the general settings are already loaded; Shopify keys just stay unset.
      }
    }

    return settings;
  }

  const {
    data: settings,
    isLoading,
    isError,
    refetch,
  } = useQuery<Settings>({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: fetchSettings,
  });

  return (
    <SettingsContext.Provider value={settings}>
      <SparkSuspense
        isLoading={isLoading || !settings}
        isError={isError}
        onReload={refetch}
        errorMessage={t('errors:generic')}
      >
        {children}
      </SparkSuspense>
    </SettingsContext.Provider>
  );
}
